import { ElectronicSignatureIcon } from '@/assets/icons/electronicSignature';
import { WetSignatureIcon } from '@/assets/icons/wetSignature';
import RadioButton from '@/components/basicComponents/radioButton';
import FooterModal from '@/components/settings-page/importData/footer';
import { useResponsive } from '@/hooks/use-responsive';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { themeVar } from '@/styles/theme';
import { SubscriptionDocumentSignatureType } from '@trace-8/trace8-schema';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { updateWizardSteps, WizardProgressBar } from '../../../basicComponents/wizard/progressBar';

enum SUBSCRIPTION_DOCUMENT_SIGNATURE_TYPE_ENUM {
  E_SIGNATURE = 'Electronic Signature',
  WET_SIGNATURE = 'Wet Signature'
}

interface SignatureMethodStepProps {
  signaturePacketWizardSteps: { name: string; status: string }[];
  handleGenerateSubscriptionDocument: (signatureType: SubscriptionDocumentSignatureType) => Promise<void>;
  handleSignaturePacketWizardSteps: (steps: { name: string; status: string }[]) => void;
}

export const SignatureMethodStep = ({
  signaturePacketWizardSteps,
  handleGenerateSubscriptionDocument,
  handleSignaturePacketWizardSteps
}: SignatureMethodStepProps) => {
  const { isMobile } = useResponsive();
  const { handleSubmit, control, watch } = useForm({ defaultValues: { signatureType: SUBSCRIPTION_DOCUMENT_SIGNATURE_TYPE_ENUM.E_SIGNATURE } });

  const onSubmit = async (data: { signatureType: SUBSCRIPTION_DOCUMENT_SIGNATURE_TYPE_ENUM }) => {
    const signatureTypeKey = getEnumKeyByValue(SUBSCRIPTION_DOCUMENT_SIGNATURE_TYPE_ENUM, data.signatureType) as SubscriptionDocumentSignatureType;
    handleGenerateSubscriptionDocument(signatureTypeKey);
  };

  return (
    <>
      <StepWrapper isMobile={isMobile}>
        <WizardProgressBar steps={signaturePacketWizardSteps} />
        <SignatureMethodStepWrapper>
          <RadioButtonsWrapper>
            <Controller
              name="signatureType"
              control={control}
              render={({ field: { value, onChange } }) => (
                <>
                  {Object.values(SUBSCRIPTION_DOCUMENT_SIGNATURE_TYPE_ENUM).map((option, index) => (
                    <RadioButton
                      key={option + index}
                      name="signatureType"
                      radioButtonText={option}
                      selectedOption={value}
                      handleRadioChange={(event) => onChange(event.target.value)}
                    />
                  ))}
                </>
              )}
            />
          </RadioButtonsWrapper>
          {watch('signatureType') === SUBSCRIPTION_DOCUMENT_SIGNATURE_TYPE_ENUM.E_SIGNATURE && (
            <BannerWrapper>
              <div>
                <ElectronicSignatureIcon />
              </div>
              <BannerContent>
                <BannerTitle>Electronic Signature</BannerTitle>
                <BannerDescription>
                  Select this option to initiate Docusign to obtain signatures. You will review, make any edits, and approve the DocuSign before
                  sending to the other recipients.
                </BannerDescription>
              </BannerContent>
            </BannerWrapper>
          )}
          {watch('signatureType') === SUBSCRIPTION_DOCUMENT_SIGNATURE_TYPE_ENUM.WET_SIGNATURE && (
            <BannerWrapper>
              <div>
                <WetSignatureIcon />
              </div>
              <BannerContent>
                <BannerTitle>Wet Signature</BannerTitle>
                <BannerDescription>
                  Docusign enables you to review and edit the signature packet. But once finalized you will be able to download it for wet signatures.
                </BannerDescription>
              </BannerContent>
            </BannerWrapper>
          )}
        </SignatureMethodStepWrapper>
      </StepWrapper>
      <FooterModal
        startOverButtonName="Back"
        startOver={() => updateWizardSteps('prev', signaturePacketWizardSteps, handleSignaturePacketWizardSteps)}
        confirmButton={{
          name: 'Generate Signature Packet',
          onClick: handleSubmit(onSubmit)
        }}
      />
    </>
  );
};

const StepWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 25px;
  margin: 30px 0 150px 0;
  padding: ${({ isMobile }) => (isMobile ? '20px 30px' : '40px 60px')};
  background: ${themeVar('layer.1')};
  border: 1px solid ${themeVar('border.base')};
  border-radius: 10px;
`;

const SignatureMethodStepWrapper = styled.div`
  margin: 0 auto;
  width: 333px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const RadioButtonsWrapper = styled.div`
  display: flex;
`;

const BannerWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const BannerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-left: 20px;
  border-left: 1px solid ${themeVar('border.base')};
`;

const BannerTitle = styled.div`
  font-size: 19px;
  font-weight: 700;
  line-height: 25px;
  color: ${themeVar('font.strong')};
`;

const BannerDescription = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  color: ${themeVar('font.base')};
`;
